// @flow
import * as React from 'react';
import { get } from 'lodash';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import type { ApiClient } from 'speed-js-react';
import { withLeaflet } from 'react-leaflet';
import BaseEditor, { SRID } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { withRouter } from 'react-router-dom';
import { HTTP_400_BAD_REQUEST, HTTP_404_NOT_FOUND } from 'speed-js-core';
import Spinner from 'esm-web-pro/components/forms/Spinner';

import type { Match, RouterHistory } from 'react-router-dom';
import { injectIntl, IntlShape } from 'react-intl';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import {
    OBJECT_COMPONENTS as NETWORK_OBJECT_COMPONENTS,
    MAP_RESOURCES as NETWORK_MAP_OBJECT_RESOURCES,
    RESOURCES as NETWORK_OBJECT_RESOURCES,
} from '../pages/network/Util';
import {
    OBJECT_COMPONENTS as COVERAGE_OBJECT_COMPONENTS,
    RESOURCES as COVERAGE_OBJECT_RESOURCES,
} from '../pages/coverage/Util';

type Props = {
    // eslint-disable-next-line react/no-unused-prop-types
    resource: string,
    // eslint-disable-next-line react/no-unused-prop-types
    identity: string,
    // eslint-disable-next-line react/no-unused-prop-types
    map: { uuid: string, identity: string, is_draft: boolean },
    getObject: ?() => Promise<Object>,
    // eslint-disable-next-line react/no-unused-prop-types
    object: ?Object,
    editor: BaseEditor,
    leaflet: LeafletContext,
    history: RouterHistory,
    match: Match,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    loading: boolean,
    panelId: String,
}

type State = {
    object: ?Object,
}


const OBJECT_RESOURCES = {
    ...NETWORK_OBJECT_RESOURCES,
    ...COVERAGE_OBJECT_RESOURCES,
};

const OBJECT_MAP_RESOURCES = {
    ...OBJECT_RESOURCES,
    ...NETWORK_MAP_OBJECT_RESOURCES,
};

const OBJECT_COMPONENTS = {
    ...NETWORK_OBJECT_COMPONENTS,
    ...COVERAGE_OBJECT_COMPONENTS,
};

// Object context
export const ObjectContext = React.createContext(null);

@connectAPI((apiClient: ApiClient, props: Props) => ({
    getObject: {
        send: () => {
            let params = {};
            const resourceName = OBJECT_MAP_RESOURCES
                ? OBJECT_MAP_RESOURCES[props.resource] || props.resource : props.resource;

            if (props.map.is_draft) {
                params = { ...params, is_draft: props.map.uuid };
            }

            const resource = get(apiClient.resources, resourceName, params);
            if (resource.isNested) {
                return resource.get(props.map.uuid, props.identity, params);
            }

            if (resourceName === 'address') {
                params = { ...params, srid: SRID };
            }

            return resource.get(props.identity, params);
        },
        success: (response) => (response ? { object: response } : null),
    },
}))
class MapObject extends React.PureComponent<Props, State> {
    static defaultProps = {
        getObject: undefined,
        object: null,
    };

    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            object: null,
            objectLayer: null,
            loadedData: true,
        };
    }

    componentDidMount() {
        const {
            getObject, leaflet: { map }, history, match, confirm, intl: { formatMessage },
        } = this.props;
        if (map) {
            map.invalidateSize();
        }
        this._isMounted = true;

        if (getObject) {
            // eslint-disable-next-line consistent-return
            getObject().then((response) => {
                if (response) {
                    if (response.status === HTTP_404_NOT_FOUND) {
                        history.push(match.url.split('/layer')[0]);
                        return {
                            errors: response.data,
                        };
                    }

                    if (response.status === HTTP_400_BAD_REQUEST) {
                        confirm({
                            message: response.data.error,
                            actions: {
                                cancel: {
                                    label: formatMessage(sidePanelMessages.okButton),
                                    callback: () => {},
                                    type: 'negative',
                                },
                            },
                        });
                    } else if (this._isMounted) {
                        this.getObjectLayer();
                        this.setState({ object: response });
                    }
                }
            });
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { loading } = this.props;

        if (prevProps.loading && !loading) {
            this.getObjectLayer();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;

        const { editor, leaflet: { map } } = this.props;

        if (map) map.highlightedGeometry = null;

        editor.focus();
    }

    getObjectLayer = () => {
        const { editor } = this.props;
        const { object } = this.state;

        if (editor && object) {
            editor.map.current.leafletElement.eachLayer((layer) => {
                if (layer.feature) {
                    const layerId = layer.feature.properties.point
                        ? layer.feature.properties.point.uuid
                        : layer.feature.id;
                    if (layerId === object.uuid || layerId === object.id) {
                        this.setState({ objectLayer: layer });
                        editor.focus(layer);
                    }
                }
            });
        }
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        let { object } = props;
        if (state.object !== object) {
            // eslint-disable-next-line prefer-destructuring
            object = state.object;
        }
        return { object };
    }

    reload = () => {
        const { getObject } = this.props;
        this.setState({ loadedData: false });
        if (getObject) {
            getObject().then((response) => {
                if (response) {
                    this.setState({ object: response, loadedData: true });
                }
            });
        }
    };

    updateObject = (data: Object) => {
        this.setState((prevState) => ({ object: { ...prevState.object, ...data } }));
    };

    render() {
        const {
            resource, map, editor, panelId,
        } = this.props;
        const { object, objectLayer, loadedData } = this.state;

        if (!object) {
            return (
                <Spinner
                    type="clipLoader"
                    loading
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'relative',
                        width: 'auto',
                        height: '100%',
                    }}
                />
            );
        }

        const resourceName = OBJECT_RESOURCES ? OBJECT_RESOURCES[resource] || resource : resource;
        const ObjectComponent = OBJECT_COMPONENTS[resource];
        return (
            <ObjectContext.Provider value={{ reload: this.reload, loading: !loadedData }}>
                <ObjectComponent
                    panelId={panelId}
                    object={object}
                    objectLayer={objectLayer}
                    map={map}
                    updateObject={this.updateObject}
                    editor={editor}
                    resource={resourceName}
                    loadedData={loadedData}
                />
            </ObjectContext.Provider>
        );
    }
}

export default withLeaflet(injectIntl(withConfirmation(withRouter(MapObject))));
